import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import './authentication.css';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const AUTH_API_BASE_URL = process.env.REACT_APP_AUTH_API_BASE_URL || `${API_BASE_URL}/api/auth`;
const ADMIN_ACCESS_CODE = process.env.REACT_APP_ADMIN_ACCESS_CODE || 'adm@123';
const ADMIN_DASHBOARD_PATH = '/admin/dashboard?role=admin';
const STUDENT_LOGIN_URL = process.env.REACT_APP_STUDENT_LOGIN_URL || `${API_BASE_URL}/api/student-app/login/`;
const COLLEGE_LOOKUP_URL = process.env.REACT_APP_COLLEGE_LOOKUP_URL || `${API_BASE_URL}/api/admin-app/college-lookup/`;
const COLLEGE_LOGIN_URL = process.env.REACT_APP_COLLEGE_LOGIN_URL || `${API_BASE_URL}/api/admin-app/college-login/`;
const QMANAGER_ACCESS_CODE = process.env.REACT_APP_QMANAGER_ACCESS_CODE || 'qmanager';

const LoginMain = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const searchParams = new URLSearchParams(location.search);
    const step = searchParams.get('step') === 'credentials' ? 'credentials' : 'college';
    const collegeCodeFromQuery = searchParams.get('college_id') || '';

    const [collegeCode, setCollegeCode] = useState('');
    const [loginId, setLoginId] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [collegeName, setCollegeName] = useState('');
    const [isLookupLoading, setIsLookupLoading] = useState(false);
    const [lookupError, setLookupError] = useState('');

    useEffect(() => {
        const code = (step === 'college' ? collegeCode : collegeCodeFromQuery).trim();

        if (!code) {
            setCollegeName('');
            setLookupError('');
            setIsLookupLoading(false);
            return;
        }

        if (code === ADMIN_ACCESS_CODE) {
            setCollegeName('Admin Access');
            setLookupError('');
            setIsLookupLoading(false);
            return;
        }

        if (code.toLowerCase() === QMANAGER_ACCESS_CODE.toLowerCase()) {
            setCollegeName('Question Manager Access');
            setLookupError('');
            setIsLookupLoading(false);
            return;
        }

        const timeoutId = setTimeout(async () => {
            setIsLookupLoading(true);
            setLookupError('');

            try {
                const response = await fetch(COLLEGE_LOOKUP_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        college_id: code,
                    }),
                });

                const data = await response.json();

                if (response.ok) {
                    setCollegeName(data.college?.college_name || '');
                    setLookupError('');
                } else {
                    setCollegeName('');
                    setLookupError(data.error || 'College not found.');
                }
            } catch (error) {
                setCollegeName('');
                setLookupError('Unable to verify college code right now.');
            } finally {
                setIsLookupLoading(false);
            }
        }, 350);

        return () => clearTimeout(timeoutId);
    }, [collegeCode, collegeCodeFromQuery, step]);

    useEffect(() => {
        if (step === 'credentials') {
            setLoginId('');
        }
    }, [collegeCodeFromQuery, step]);

    const handleNext = (event) => {
        event.preventDefault();

        if (!collegeCode.trim()) {
            swal('Oops', 'College code is required.', 'error');
            return;
        }

        if (!collegeName) {
            swal('Oops', 'Please enter a valid college code first.', 'error');
            return;
        }

        navigate(`/login?step=credentials&college_id=${encodeURIComponent(collegeCode.trim())}`);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const selectedCollegeCode = (collegeCodeFromQuery || '').trim();
        const normalizedLoginId = (loginId || '').trim();
        const isAdminLoginAttempt = normalizedLoginId === ADMIN_ACCESS_CODE;
        const isQuestionManagerCode = selectedCollegeCode.toLowerCase() === QMANAGER_ACCESS_CODE.toLowerCase();

        if (!selectedCollegeCode) {
            swal('Oops', 'College code is required. Please restart login from step 1.', 'error');
            return;
        }

        if (!normalizedLoginId || !password) {
            swal('Oops', 'Email/College ID and password are required.', 'error');
            return;
        }

        if (isAdminLoginAttempt && selectedCollegeCode !== ADMIN_ACCESS_CODE) {
            swal('Oops', 'Invalid username or password', 'error');
            return;
        }

        // Enforce that institution login uses the same code selected in step 1.
        if (!normalizedLoginId.includes('@') && !isAdminLoginAttempt && !isQuestionManagerCode && normalizedLoginId !== selectedCollegeCode) {
            swal('Oops', 'Invalid username or password', 'error');
            return;
        }

        setIsSubmitting(true);
        swal({
            title: 'Logging in...',
            text: 'Please wait',
            buttons: false,
            closeOnClickOutside: false,
            closeOnEsc: false,
        });
        try {
            let data = {};
            let studentData = {};

            if (isAdminLoginAttempt) {
                const response = await fetch(`${AUTH_API_BASE_URL}/login/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: normalizedLoginId,
                        password,
                        college_code: selectedCollegeCode,
                    }),
                });

                data = await response.json();

                if (response.ok) {
                    localStorage.setItem('guest_user_role', 'admin');
                    sessionStorage.setItem('guest_user_role', 'admin');
                    localStorage.setItem('guest_access_token', data.tokens?.access || '');
                    localStorage.setItem('guest_refresh_token', data.tokens?.refresh || '');
                    sessionStorage.setItem('guest_access_token', data.tokens?.access || '');
                    sessionStorage.setItem('guest_refresh_token', data.tokens?.refresh || '');
                    localStorage.setItem('guest_admin_user', JSON.stringify(data.user || null));
                    localStorage.removeItem('guest_student_id');

                    swal({
                        title: 'Redirecting...',
                        text: 'Opening admin dashboard',
                        buttons: false,
                        closeOnClickOutside: false,
                        closeOnEsc: false,
                    });
                    window.location.href = ADMIN_DASHBOARD_PATH;
                    return;
                }
            }

            if (isQuestionManagerCode) {
                const qmanagerResponse = await fetch(`${AUTH_API_BASE_URL}/login/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: normalizedLoginId,
                        password,
                        college_code: selectedCollegeCode,
                    }),
                });

                const qmanagerData = await qmanagerResponse.json();

                if (qmanagerResponse.status === 403) {
                    localStorage.removeItem('guest_user_role');
                    sessionStorage.removeItem('guest_user_role');
                    swal.close();
                    swal('Pending Approval', qmanagerData.error || 'Wait for admin approval', 'warning');
                    return;
                }

                if (qmanagerResponse.ok) {
                    localStorage.setItem('guest_user_role', 'question-manager');
                    sessionStorage.setItem('guest_user_role', 'question-manager');
                    localStorage.setItem('guest_access_token', qmanagerData.tokens?.access || '');
                    localStorage.setItem('guest_refresh_token', qmanagerData.tokens?.refresh || '');

                    swal.close();
                    window.location.href = '/dashboard/institution/question-manager?role=question-manager';
                    return;
                }

                swal.close();
                swal('Oops', 'Invalid username or password', 'error');
                return;
            }

            if (normalizedLoginId.includes('@') && !isAdminLoginAttempt) {
                const studentResponse = await fetch(STUDENT_LOGIN_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        email: normalizedLoginId,
                        password,
                        college_code: selectedCollegeCode,
                    }),
                });

                studentData = await studentResponse.json();

                if (studentResponse.status === 403) {
                    localStorage.removeItem('guest_user_role');
                    sessionStorage.removeItem('guest_user_role');
                    localStorage.removeItem('guest_student_id');
                    swal.close();
                    swal('Pending Approval', studentData.error || 'Wait for approval', 'warning');
                    return;
                }

                if (studentResponse.ok) {
                    const confirmationStatus = (studentData.student?.confirmation_status || '').toLowerCase();
                    if (confirmationStatus === 'pending') {
                        localStorage.removeItem('guest_user_role');
                        sessionStorage.removeItem('guest_user_role');
                        localStorage.removeItem('guest_student_id');
                        swal.close();
                        swal('Pending Approval', 'Wait for approval', 'warning');
                        return;
                    }

                    localStorage.setItem('guest_user_role', 'student');
                    sessionStorage.setItem('guest_user_role', 'student');
                    localStorage.setItem('guest_student_id', String(studentData.student?.id || ''));
                    localStorage.setItem('guest_student_name', String(studentData.student?.username || ''));
                    localStorage.setItem('guest_student_email', String(studentData.student?.email || ''));
                    localStorage.setItem('guest_college_code', String(studentData.student?.college_code || selectedCollegeCode));
                    sessionStorage.setItem('guest_college_code', String(studentData.student?.college_code || selectedCollegeCode));
                    localStorage.removeItem('guest_access_token');
                    localStorage.removeItem('guest_refresh_token');

                    swal.close();
                    window.location.href = '/dashboard/student?role=student';
                    return;
                }
            }

            const collegeResponse = await fetch(COLLEGE_LOGIN_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    college_id: normalizedLoginId,
                    access_code: selectedCollegeCode,
                    password,
                }),
            });

            const collegeData = await collegeResponse.json();

            if (collegeResponse.ok) {
                localStorage.setItem('guest_user_role', 'institution');
                sessionStorage.setItem('guest_user_role', 'institution');
                localStorage.setItem('guest_college_code', String(collegeData.college?.college_id || ''));
                sessionStorage.setItem('guest_college_code', String(collegeData.college?.college_id || ''));
                localStorage.setItem('guest_college_name', String(collegeData.college?.college_name || ''));
                sessionStorage.setItem('guest_college_name', String(collegeData.college?.college_name || ''));
                localStorage.removeItem('guest_access_token');
                localStorage.removeItem('guest_refresh_token');

                swal.close();
                window.location.href = '/dashboard/institution?role=institution';
                return;
            }

            swal.close();
            swal('Oops', 'Invalid username or password', 'error');
        } catch (error) {
            swal.close();
            swal('Oops', 'Unable to reach the server. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFormSubmit = (event) => {
        if (step === 'college') {
            handleNext(event);
            return;
        }

        handleSubmit(event);
    };

    const isQuestionManagerCredentials = (collegeCodeFromQuery || '').trim().toLowerCase() === QMANAGER_ACCESS_CODE.toLowerCase();

    return (
        <>
            <div className="react-login-page react-signup-page pt---120 pb---120">
                <div className="container">
                    <div className="row">                            
                        <div className="col-lg-12">
                            <div className="login-right-form">
                                <form onSubmit={handleFormSubmit}>
                                    <div className="login-top">
                                        <h3>Login</h3>
                                        <p>Don't have an account yet? <a href="signup.html">Sign up for free</a></p>
                                    </div>
                                    {step === 'college' ? (
                                        <>
                                            <p>
                                                <label>Access Code</label>
                                                {collegeName ? (
                                                    <div style={{ fontWeight: 700, marginBottom: '8px' }}>
                                                        {collegeName}
                                                    </div>
                                                ) : null}
                                                <input
                                                    placeholder="Enter access code"
                                                    type="text"
                                                    id="college-code"
                                                    name="college-code"
                                                    value={collegeCode}
                                                    onChange={(event) => setCollegeCode(event.target.value)}
                                                />
                                                {isLookupLoading ? (
                                                    <small style={{ display: 'block', marginTop: '8px' }}>Checking college code...</small>
                                                ) : null}
                                                {!isLookupLoading && lookupError ? (
                                                    <small style={{ display: 'block', marginTop: '8px', color: '#d93025' }}>{lookupError}</small>
                                                ) : null}
                                            </p>
                                            <button
                                                type="submit"
                                                id="button"
                                                name="submit"
                                                disabled={isSubmitting || isLookupLoading}
                                            >
                                                {isLookupLoading ? 'Checking...' : 'Next'}{' '}
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-arrow-right"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <p>
                                                <label>{isQuestionManagerCredentials ? 'Email / Username' : 'Email'}</label>
                                                <input
                                                    placeholder={isQuestionManagerCredentials ? 'Enter email or username' : 'Email or College ID'}
                                                    type="text"
                                                    id="login-id"
                                                    name="login-id"
                                                    value={loginId}
                                                    onChange={(event) => setLoginId(event.target.value)}
                                                />
                                            </p>
                                            <p>
                                                <label>Password</label>
                                                <div className="password-input-wrapper">
                                                    <input
                                                        placeholder="Password"
                                                        type={showPassword ? 'text' : 'password'}
                                                        id="pass"
                                                        name="pass"
                                                        value={password}
                                                        onChange={(event) => setPassword(event.target.value)}
                                                        disabled={isSubmitting}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="password-toggle-btn"
                                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                                        onClick={() => setShowPassword((prev) => !prev)}
                                                        disabled={isSubmitting}
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                            <path d="M1.5 12C3.9 7.8 7.6 5.7 12 5.7C16.4 5.7 20.1 7.8 22.5 12C20.1 16.2 16.4 18.3 12 18.3C7.6 18.3 3.9 16.2 1.5 12Z" stroke="currentColor" strokeWidth="1.8" />
                                                            <circle cx="12" cy="12" r="3.2" stroke="currentColor" strokeWidth="1.8" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </p>
                                            <div className="back-check-box">
                                                <input type="checkbox" id="box-1" /> Remember me
                                                <p>Forget password?</p>
                                            </div>
                                            <button type="submit" id="button" name="submit" disabled={isSubmitting}>
                                                {isSubmitting ? 'Logging in...' : 'LogIn'}{' '}
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="feather feather-arrow-right"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                                            </button>
                                            <span className="back-or"> ........ or Login with ........ </span>
                                            <ul className="social-links">
                                                <li>
                                                    <button type="button" aria-label="Login with Facebook" className="social-icon-btn">
                                                        <span aria-hidden="true" className="social_facebook"></span>
                                                    </button>
                                                </li>
                                                <li>
                                                    <button type="button" aria-label="Login with Twitter" className="social-icon-btn">
                                                        <span aria-hidden="true" className="social_twitter"></span>
                                                    </button>
                                                </li>
                                            </ul>
                                        </>
                                    )}
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>   
        </>

    );
}


export default LoginMain;